import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { find, get, isEmpty, wget } from 'lodash';
import FacesheetInteractions from 'src/components/Facesheet/Overview/components/FacesheetInteractions';
import { fetchServiceCases } from 'actions/Case/Contact/Group';
import CaseSummary from 'src/components/Facesheet/Overview/components/CaseSummary';
import {
  cl1224UnconsentedClientFacesheetAccess as cl1224UnconsentedClientFacesheetAccessFF,
} from 'common/utils/FeatureFlags/flags';
import Relationships from './Relationships/Relationships';

const DISPLAY_NAME = 'facesheet-overview';
export function FacesheetOverview({
  contact,
  contactCases,
  employeeId,
  getClientRelationship,
  visibilityConfig,
  cl1224UnconsentedClientFacesheetAccess,
  isClientsDrawer,
  shouldPortalizeDialogs,
  groupId,
  fetchServiceCases: fetchServiceCasesAction,
  route,
}) {
  if (isEmpty(contact)) {
    return null;
  }
  const contactConsented = contact?.consent?.state === 'accepted';
  const shouldShowConsentWarning = !contactConsented && cl1224UnconsentedClientFacesheetAccess;

  const contactId = contact?.id;

  useEffect(() => {
    if (groupId && contactId && route) {
      fetchServiceCasesAction(groupId, contactId);
    }
  }, [groupId, contactId, route, fetchServiceCasesAction]);

  return (
    <div className={DISPLAY_NAME}>
      {shouldShowConsentWarning && (
        <div className={isClientsDrawer ? 'pt-6' : ''}>
          <div
            className="
              flex justify-between items-center h-52px px-4 mb-4 -mt-4
              rounded border border-solid border-yellow bg-light-yellow
            "
          >
            This client hasn&apos;t provided consent to send referrals yet, so you&apos;re seeing a limited view of
            their face sheet. Capture their consent to see additional information.
          </div>
        </div>
      )}
      <div className="row">
        <div className={!isClientsDrawer ? 'col-md-4' : 'col-md-12'}>
          {contact && (
            <Relationships
              person={contact}
              employeeId={employeeId}
              getClientRelationship={getClientRelationship}
              visibilityConfig={visibilityConfig}
              isClientsDrawer={isClientsDrawer}
              shouldPortalizeDialogs={shouldPortalizeDialogs}
            />
          )}
          {visibilityConfig.overview.showOpenCases && (
            <div className={`${isClientsDrawer ? 'mb-4' : 'mb-6'}`}>
              {!isEmpty(contactCases) && (
                <CaseSummary contactCases={contactCases} />
              )}
            </div>
          )}
        </div>
        <div className={!isClientsDrawer ? 'col-md-8' : 'col-md-12'}>
          <FacesheetInteractions
            contact={contact}
            contactCases={contactCases}
            visibilityConfig={visibilityConfig}
            isClientsDrawer={isClientsDrawer}
            shouldPortalizeDialogs={shouldPortalizeDialogs}
          />
        </div>
      </div>
    </div>
  );
}

const OverviewVisibilityConfigPropType = PropTypes.shape({
  showTab: PropTypes.bool.isRequired,
  showCareCoordinator: PropTypes.bool.isRequired,
  showFamilyMembers: PropTypes.bool.isRequired,
  showOpenCases: PropTypes.bool.isRequired,
  showNotes: PropTypes.bool.isRequired,
  showMessages: PropTypes.bool.isRequired,
});

FacesheetOverview.propTypes = {
  contact: PropTypes.object.isRequired,
  contactCases: PropTypes.array.isRequired,
  params: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  employeeId: PropTypes.string,
  getClientRelationship: PropTypes.func.isRequired,
  visibilityConfig: PropTypes.shape({
    overview: OverviewVisibilityConfigPropType.isRequired,
  }).isRequired,
  cl1224UnconsentedClientFacesheetAccess: PropTypes.bool.isRequired,
  isClientsDrawer: PropTypes.bool.isRequired,
  shouldPortalizeDialogs: PropTypes.bool,
  fetchServiceCases: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  route: PropTypes.object,
};

FacesheetOverview.defaultProps = {
  employeeId: '',
  shouldPortalizeDialogs: false,
  route: undefined,
};

function mapStateToProps(state, ownProps) {
  const isClientsDrawer = ownProps.isClientsDrawer;
  if (isClientsDrawer) {
    const contactId = wget(ownProps, 'id', '');
    const contact = wget(ownProps, 'contact', {});
    const contactCaseObj = get(state.serviceCase, contactId);
    const contactCases = get(contactCaseObj, 'data', []);
    const groupId = get(state, 'session.groupId', '');
    const employeeId = get(state, 'globalState.currentEmployee.id', '');
    return {
      contact,
      contactCases,
      groupId,
      employeeId,
      cl1224UnconsentedClientFacesheetAccess: cl1224UnconsentedClientFacesheetAccessFF(state),
      isClientsDrawer,
      shouldPortalizeDialogs: ownProps.shouldPortalizeDialogs,
    };
  }
  const contactId = wget(ownProps, 'params.id', '');
  const contacts = wget(state, 'contacts.contacts', []);
  const contactCaseObj = get(state.serviceCase, contactId);
  const contactCases = get(contactCaseObj, 'data', []);
  const groupId = get(state, 'session.groupId', '');
  const employeeId = get(state, 'globalState.currentEmployee.id', '');
  return {
    contact: find(contacts, { id: contactId }),
    contactCases,
    groupId,
    employeeId,
    cl1224UnconsentedClientFacesheetAccess: cl1224UnconsentedClientFacesheetAccessFF(state),
    isClientsDrawer,
    shouldPortalizeDialogs: ownProps.shouldPortalizeDialogs,
  };
}

export default connect(mapStateToProps, {
  fetchServiceCases,
})(FacesheetOverview);
